if (!customElements.get('product-upsell-form')) {
  class ProductUpsellForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.submitButton = this.form?.querySelector('[type="submit"]');

      if (!this.form || !this.submitButton) {
        return;
      }

      this.form.addEventListener('submit', this.#onSubmit.bind(this));
    }

    #getFlyMedia() {
      if (typeof window.getUpsellFlyMedia === 'function') {
        return window.getUpsellFlyMedia(this);
      }

      return { title: '', imageSrc: '' };
    }

    #beginOptimisticFly() {
      const media = this.#getFlyMedia();

      if (typeof window.startOptimisticFlyToCart !== 'function') {
        this.#showSubmitLoading();
        return;
      }

      this._flySessionPromise = window
        .startOptimisticFlyToCart(this, {
          originEl: this.submitButton,
          media,
        })
        .then((session) => {
          this._flySession = session ?? null;

          if (!session) {
            this.#showSubmitLoading();
          }

          return session;
        });
    }

    #showSubmitLoading() {
      this.submitButton.classList.add('loading');
      this.submitButton.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
    }

    #finishFlySession() {
      if (this.error) {
        this._flySession?.fail();
        this._pendingCartCountUpdate = null;
      }

      this._flySession = null;
      this._flySessionPromise = null;
    }

    #updateCartSections(response) {
      const pending = scheduleCartSectionsUpdateFromResponse(response, {
        error: this.error,
        flySession: this._flySession,
        flySessionPromise: this._flySessionPromise,
      });

      if (pending) {
        this._pendingCartCountUpdate = pending;
      }
    }

    #onSubmit(evt) {
      evt.preventDefault();

      const idInput = this.form.querySelector('[name="id"]');

      if (!idInput?.value || this.submitButton.getAttribute('aria-disabled') === 'true') {
        return;
      }

      const spinner = this.submitButton.querySelector('.loading-overlay__spinner');

      this.error = false;
      this.submitButton.setAttribute('aria-disabled', 'true');

      this._flySession = null;
      this._pendingCartCountUpdate = null;
      this.#beginOptimisticFly();

      const formData = new FormData(this.form);
      formData.append('sections', 'cart-drawer,header');
      formData.append('sections_url', window.location.pathname);

      fetch(`${window.routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },
        body: formData,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Network response was not ok: ${response.status}`);
          }

          return response.json();
        })
        .then((response) => {
          if (response.status) {
            console.error(response.description);
            this.error = true;
            return;
          }

          this.#updateCartSections(response);

          this.dispatchEvent(
            new CustomEvent('product-upsell:submit:success', {
              bubbles: true,
              detail: {
                response,
                media: this.#getFlyMedia(),
              },
            }),
          );
        })
        .catch((error) => {
          console.error(error);
          this.error = true;
        })
        .finally(() => {
          this.#finishFlySession();
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');

          if (spinner) {
            spinner.classList.add('hidden');
          }
        });
    }
  }

  customElements.define('product-upsell-form', ProductUpsellForm);
}

if (!customElements.get('product-upsell-carousel')) {
  class ProductUpsellCarousel extends HTMLElement {
    #index = -1;

    #frame = 0;

    #settleTimer = 0;

    #onScroll = null;

    #onScrollEnd = null;

    #onPrev = null;

    #onNext = null;

    connectedCallback() {
      this.scroller = this.querySelector('[data-product-upsell-list]');
      this.slides = Array.from(this.querySelectorAll('[data-upsell-slide]'));
      this.prevButton = this.querySelector('[data-upsell-prev]');
      this.nextButton = this.querySelector('[data-upsell-next]');
      this.fractionEl = this.querySelector('[data-upsell-fraction]');
      this.statusEl = this.querySelector('[data-upsell-status]');

      if (!this.scroller || this.slides.length < 2) {
        return;
      }

      this.#onScroll = () => {
        if (this.#frame) {
          return;
        }

        this.#frame = requestAnimationFrame(() => {
          this.#frame = 0;
          this.#updatePosition(false);
        });

        clearTimeout(this.#settleTimer);
        this.#settleTimer = setTimeout(() => {
          this.#updatePosition(true);
        }, 150);
      };

      this.#onScrollEnd = () => {
        clearTimeout(this.#settleTimer);
        this.#updatePosition(true);
      };

      this.#onPrev = () => {
        this.#go(-1);
      };

      this.#onNext = () => {
        this.#go(1);
      };

      this.scroller.addEventListener('scroll', this.#onScroll, { passive: true });
      this.scroller.addEventListener('scrollend', this.#onScrollEnd);
      this.prevButton?.addEventListener('click', this.#onPrev);
      this.nextButton?.addEventListener('click', this.#onNext);
      this.#updatePosition(true);
    }

    disconnectedCallback() {
      if (!this.scroller || !this.#onScroll) {
        return;
      }

      this.scroller.removeEventListener('scroll', this.#onScroll);
      this.scroller.removeEventListener('scrollend', this.#onScrollEnd);
      this.prevButton?.removeEventListener('click', this.#onPrev);
      this.nextButton?.removeEventListener('click', this.#onNext);

      if (this.#frame) {
        cancelAnimationFrame(this.#frame);
      }

      clearTimeout(this.#settleTimer);
    }

    #go(delta) {
      const next = this.#index + delta;

      if (next < 0 || next >= this.slides.length) {
        return;
      }

      const slide = this.slides[next];
      const left =
        slide.getBoundingClientRect().left - this.scroller.getBoundingClientRect().left + this.scroller.scrollLeft;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.scroller.scrollTo({
        left,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    #currentIndex() {
      const scrollerLeft = this.scroller.getBoundingClientRect().left;
      let best = 0;
      let bestDistance = Infinity;

      for (const [index, slide] of this.slides.entries()) {
        const distance = Math.abs(slide.getBoundingClientRect().left - scrollerLeft);

        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      }

      return best;
    }

    #updatePosition(settle) {
      const index = this.#currentIndex();
      const indexChanged = index !== this.#index;

      this.#index = index;

      if (indexChanged) {
        const current = index + 1;
        const total = this.slides.length;

        if (this.fractionEl) {
          this.fractionEl.textContent = `${current}/${total}`;
        }

        if (this.statusEl) {
          const template = this.statusEl.getAttribute('data-status-template') || '';

          this.statusEl.textContent = template.replace('__CURRENT__', String(current));
        }

        if (this.prevButton) {
          this.prevButton.disabled = index === 0;
        }

        if (this.nextButton) {
          this.nextButton.disabled = index === total - 1;
        }
      }

      if (!settle) {
        return;
      }

      for (const [slideIndex, slide] of this.slides.entries()) {
        const active = slideIndex === this.#index;

        slide.toggleAttribute('inert', !active);

        if (active) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      }
    }
  }

  customElements.define('product-upsell-carousel', ProductUpsellCarousel);
}
